import { useEffect, useCallback } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { useAppDispatch } from '@/utils/typeHelpers'
import { UploadDocumentType, UserDocumentCategoryIdentifier } from '@/constants'
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import {
  CP261Tooltip,
  EinLetterTooltip,
  Form2553Tooltip,
  IncorporationDocTooltip,
  StateRegistrationTooltip,
} from '../PracticeProfileTooltips'

import { UploadItem } from '../shared/UploadItem'
import { TextColors } from '@/styles'
import { Link, Text } from '@/ui'

interface IDocumentUploads {
  businessEntity?: string
  isScorp?: boolean
}

export const DocumentUploads = ({
  businessEntity,
  isScorp,
}: IDocumentUploads) => {
  const dispatch = useAppDispatch()

  const refetchDocuments = useCallback(() => {
    dispatch(fetchUserDocuments(false, true))
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  if (isScorp) {
    return (
      <>
        <UploadItem
          label="Form 2553"
          popUpContent={<Form2553Tooltip />}
          documentCategory={UserDocumentCategoryIdentifier.form2553}
          additionalTrackingProperties={{
            document_category: 'form_2553',
            business_entity: businessEntity,
          }}
          onUploadSuccess={refetchDocuments}
        >
          <Text>
            This is the form you used to apply for S Corporation status. If
            you&apos;ve lost your Form 2553, you can&apos;t request another copy
            of it. However, you can request an S Corporation Verification Letter
            (385C) by calling the IRS at 1-800-829-4933. You can upload either
            here.
          </Text>

          <Link
            className="mt-2"
            href="https://www.irs.gov/pub/irs-pdf/f2553.pdf"
            color={TextColors.BRAND}
            iconRight={regular('external-link-alt')}
            newPage
          >
            Download Form
          </Link>
        </UploadItem>

        <UploadItem
          label="CP261 Letter"
          popUpContent={<CP261Tooltip />}
          documentCategory={UserDocumentCategoryIdentifier.cp261Notice}
          additionalTrackingProperties={{
            document_category: 'cp261_letter',
            business_entity: businessEntity,
          }}
          onUploadSuccess={refetchDocuments}
        >
          <Text>
            The CP261 Notice is an official confirmation of your S Corporation
            election approval status. If you&apos;ve lost your CP261 Notice, you
            can&apos;t request another copy of it. However, you can request an S
            Corporation verification letter (known as Form 385C) by calling the
            IRS at 1-800-829-4933. You can upload either here.
          </Text>

          <Link
            className="mt-2"
            href="https://www.irs.gov/pub/irs-pdf/f2553.pdf"
            color={TextColors.BRAND}
            iconRight={regular('external-link-alt')}
            newPage
          >
            Download Form
          </Link>
        </UploadItem>

        <UploadItem
          label="Business Incorporation Document"
          popUpContent={<IncorporationDocTooltip />}
          documentCategory={
            UserDocumentCategoryIdentifier.businessIncorporation
          }
          documentType={UploadDocumentType.INCORPORATION}
          additionalTrackingProperties={{
            document_category: 'incorporation_document',
            business_entity: businessEntity,
          }}
          onUploadSuccess={refetchDocuments}
        >
          <Text>
            Articles of incorporation are formal documents filed with a
            governmental body to legally establish a corporation. They typically
            contain relevant information such as the corporation&apos;s name,
            street address, agent for service of process, and the quantity and
            type of stock to be issued.
          </Text>
        </UploadItem>
      </>
    )
  }

  return (
    <>
      <UploadItem
        label="EIN Letter"
        popUpContent={<EinLetterTooltip />}
        documentCategory={UserDocumentCategoryIdentifier.einDocuments}
        additionalTrackingProperties={{
          document_category: 'ein_letter',
          business_entity: businessEntity,
        }}
        onUploadSuccess={refetchDocuments}
      >
        <Text>
          The EIN Verification Letter is an official IRS document that confirms
          the unique nine-digit EIN (Employer Identification Number) assigned to
          your business for tax filing and reporting purposes. This only applies
          to S Corporation customers.
        </Text>
      </UploadItem>

      <UploadItem
        label="State Registration Documents"
        popUpContent={<StateRegistrationTooltip />}
        documentCategory={UserDocumentCategoryIdentifier.stateRegistration}
        documentType={UploadDocumentType.INCORPORATION}
        additionalTrackingProperties={{
          document_category: 'state_registration',
          business_entity: businessEntity,
        }}
        onUploadSuccess={refetchDocuments}
      >
        <Text>State registrations for all states that you practice in.</Text>
      </UploadItem>
    </>
  )
}
